import BaseInput from "@/components/common/baseInput";
import {
    GetInterfaceTransactionResponse,
    InterfaceTransactionFilterRequest,
    InterfaceTransactionResponse,
} from "@/models/interfaces";
import { InputAdornment } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import BaseButton from "@/components/common/baseButton";
import { checkPermissionsWithAlert, formatAmount, formatDate, formatDateTime } from "@/util";
import api from "@/services/api";
import { PermissionName } from "@/models/user";
import popupService from "@/services/popupService";
import { useParams, useNavigate } from "react-router-dom";
import { TransactionType } from "@/models/enums";
import ArrowElement from "@/assets/images/ArrowElement.svg";

const InterfacesTransaction = () => {
    const { t } = useTranslation();
    const { interfaceId } = useParams();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState<InterfaceTransactionResponse[]>([]);
    const [hasNext, setHasNext] = useState(true);
    const [searchCounter, setSearchCounter] = useState(0);
    const [filter, setFilter] = useState<InterfaceTransactionFilterRequest>({
        interfaceId: Number(interfaceId),
        keywords: "",
        orderBy: "",
        asc: false,
        pageIndex: 0,
    });

    async function onClickSearch() {
        setTransactions([]);
        popupService.showLoading();
        setFilter((prevData) => ({
            ...prevData,
            pageIndex: 0,
        }));
        setSearchCounter(searchCounter + 1);
    }

    async function getInterfaceTransactions(): Promise<GetInterfaceTransactionResponse> {
        const responsePromise = api.getInterfaceTransactions(filter);
        return (await responsePromise).data;
    }

    let startFetchingBusy = false;
    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.InterfaceManagement)) {
            popupService.close();
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            const apiResponse = await getInterfaceTransactions();
            setHasNext(apiResponse.hasNext);
            if (filter.pageIndex === 0) {
                setTransactions(apiResponse.rows);
            } else {
                setTransactions((oldArray) => [...oldArray, ...apiResponse.rows]);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            setFilter((prevData) => ({
                ...prevData,
                interfaceId: Number(interfaceId),
            }));
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [filter.pageIndex, searchCounter]);

    const handleScroll = useCallback(() => {
        const interfacesContainer = document.getElementById("interfaces-list-grid");
        const doScroll =
            interfacesContainer &&
            interfacesContainer.scrollTop + interfacesContainer.clientHeight >= interfacesContainer.scrollHeight &&
            interfacesContainer.scrollTop > 0;
        if (!doScroll || !hasNext) {
            return;
        }
        setFilter((prevData) => ({
            ...prevData,
            pageIndex: prevData.pageIndex + 1,
        }));
    }, [filter.pageIndex, hasNext]);

    useEffect(() => {
        const interfacesContainer = document.getElementById("interfaces-list-grid");
        if (!interfacesContainer) {
            return;
        }
        interfacesContainer.addEventListener("scroll", handleScroll);
        return () => {
            interfacesContainer.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    function handleClearFilters() {
        setFilter((prevData) => ({
            ...prevData,
            keywords: "",
        }));
        setSearchCounter(searchCounter + 1);
    }

    return (
        <div className="interfaces-container">
            <div className="interfaces-header-container">
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 15,
                    }}
                >
                    <div
                        style={{ display: "flex", alignItems: "center" }}
                        className="basic-button"
                        onClick={() => navigate("/settings/manage-interface")}
                    >
                        <img className="back-to-list-arrow" src={ArrowElement} alt="ArrowElement" />
                        <div style={{ display: "inline-block", marginInlineStart: 10 }}>
                            {t("settings.BackToImportHistory")}
                        </div>
                    </div>
                </div>
            </div>
            <div className="interfaces-filter">
                <div className="font-style employee-span">{t("employee.FilterHeader")}</div>
                <div className="interfaces-filter-first-row">
                    <div className="interfaces-filter-fields">
                        <div>
                            <BaseInput
                                id="keyword-input"
                                className="search-input"
                                placeholder={`${t("interfaces.transactionsSearch")}`}
                                type="search"
                                size="small"
                                value={filter.keywords}
                                onChange={(event) =>
                                    setFilter((prevData) => ({
                                        ...prevData,
                                        keywords: event.target.value,
                                    }))
                                }
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    width: "100%",
                                }}
                            />
                        </div>
                    </div>
                    <div className="filter-search-btn">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                            }}
                        >
                            <span
                                onClick={handleClearFilters}
                                className="link-element clear-all-btn"
                                style={{
                                    minWidth: "70px",
                                }}
                            >
                                {t("display.ClearAll")}
                            </span>
                        </div>
                        <BaseButton
                            style={{ padding: "0px 30px" }}
                            className="basic-button"
                            onClick={() => onClickSearch()}
                        >
                            {t("general.filter")}
                        </BaseButton>
                    </div>
                </div>
            </div>
            <div>
                <div id="interfaces-list-grid" className="interfaces-list-grid">
                    <div className="interfaces-fake-thead"></div>
                    <table>
                        <thead>
                            <tr>
                                <td style={{ width: "10%" }}>
                                    <div className="table-number-format-name">{t("transaction.TransactionId")}</div>
                                </td>
                                <td style={{ width: "13%" }}>
                                    <div className="table-number-format-name">{t("employee.EmployeeNumber")}</div>
                                </td>
                                <td style={{ width: "9%" }}>
                                    <div style={{ marginInlineStart: "25px" }}>{t("orders.employeeName")}</div>
                                </td>
                                <td style={{ width: "9%" }}>
                                    <div style={{ marginInlineStart: "25px" }}>{t("interfaces.user")}</div>
                                </td>
                                <td style={{ width: "13%" }}>
                                    <div className="table-number-format-name">{t("interfaces.creationDate")}</div>
                                </td>
                                <td style={{ width: "14%" }}>
                                    <div className="table-number-format-name">
                                        {t("interfaces.UpdatedExpirationDate")}
                                    </div>
                                </td>
                                <td style={{ width: "10%" }}>{t("employee.Wallet.Amount")}</td>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.map((transaction) => (
                                <tr id="employees-table-row" key={transaction.id}>
                                    <td>
                                        <div className="table-number-format-value">{transaction.id}</div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-value">{transaction.employeeNumber}</div>
                                    </td>
                                    <td>
                                        <div style={{ marginInlineStart: "25px" }}>{transaction.employeeName}</div>
                                    </td>
                                    <td>
                                        <div style={{ marginInlineStart: "25px" }}>{transaction.username}</div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-value">
                                            {transaction.createdAt ? formatDateTime(transaction.createdAt) : ""}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="table-number-format-value">
                                            {transaction.updatedExpirationDate
                                                ? formatDate(transaction.updatedExpirationDate)
                                                : ""}
                                        </div>
                                    </td>
                                    <td>
                                        <div className={`transaction-amount ${transaction.transactionType}`}>
                                            {formatAmount(transaction.amount, transaction.currencyId)}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default InterfacesTransaction;
